import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { autoResizeTextarea } from '../shared/ui';
import { showRegistrationPrompt } from '../shared/navigation';
import { apiFetch, ApiError } from '../api/client';
import { sessionStore } from '../session/session-store';
import { SessionManager } from '../session/sessions';
import { registerDeferredDeliveries, forgetDeferredDeliveries } from '../session/deferred-unread';

/**
 * Отложенная услуга в интерфейсе Разговора (OG-2b).
 *
 * Заказ уходит дорогой модели в фоне, ответ сервер дозаписывает прямо в сессию
 * — клиенту остаётся узнать о доставке. Ответ идёт часами, поэтому опрос раз в
 * минуту: чаще — впустую греть сервер.
 */

const ORDERS_POLL_MS = 60_000;

interface DeferredConfig {
  enabled: boolean;
  promise_hours: number;
}

interface DeferredOrder {
  run_id: string;
  status: string;
  session_id: string | null;
  message_id: string | null;
  deadline_at: number | null;
  created_at: string | number | null;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
/** Последний известный статус заказа: доставку ловим по переходу в completed. */
const knownStatus = new Map<string, string>();

function orderButton(): HTMLButtonElement | null {
  return $<HTMLButtonElement>('deferred-order-btn');
}

export function initDeferredOrder(): void {
  orderButton()?.addEventListener('click', () => void placeDeferredOrder());
}

/** Услуга может быть выключена — тогда кнопки в интерфейсе просто нет. */
export async function startDeferredWatch(): Promise<void> {
  stopDeferredWatch();
  if (!state.authToken) return;

  let enabled: boolean;
  try {
    enabled = (await apiFetch<DeferredConfig>('/api/deferred/config')).enabled === true;
  } catch {
    enabled = false;
  }
  orderButton()?.classList.toggle('hidden', !enabled);
  if (!enabled) return;

  await refreshDeferredOrders();
  pollTimer = setInterval(() => void refreshDeferredOrders(), ORDERS_POLL_MS);
}

export function stopDeferredWatch(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  knownStatus.clear();
  forgetDeferredDeliveries();
  orderButton()?.classList.add('hidden');
}

async function refreshDeferredOrders(): Promise<void> {
  if (!state.authToken) return;

  let orders: DeferredOrder[];
  try {
    orders = (await apiFetch<{ orders: DeferredOrder[] }>('/api/deferred/orders')).orders || [];
  } catch {
    return;
  }

  let deliveredNow = false;
  for (const order of orders) {
    const previous = knownStatus.get(order.run_id);
    knownStatus.set(order.run_id, order.status);
    if (order.status !== 'completed') continue;
    if (order.session_id && order.message_id) {
      registerDeferredDeliveries(order.session_id, [order.message_id]);
    }
    // Заказ, уже завершённый к первому опросу, — не новость: значок «есть
    // новое» о нём скажет сам, а перечитывать список из-за него незачем.
    if (previous && previous !== 'completed') deliveredNow = true;
  }

  if (deliveredNow) {
    await SessionManager.loadList();
    const activeId = sessionStore.activeSessionId;
    const inConversation = document.querySelector('.view.active')?.id === 'view-conversation';
    if (inConversation && activeId && orders.some((o) => o.status === 'completed' && o.session_id === activeId)) {
      await SessionManager.loadSession(activeId);
    }
  }
  SessionManager.refreshSessionIndicators();
}

async function placeDeferredOrder(): Promise<void> {
  const input = $<HTMLTextAreaElement>('user-input');
  const question = input?.value.trim();
  if (!input || !question) return;

  let session = sessionStore.getActiveSession();
  if (!session) {
    session = sessionStore.setActiveSession(crypto.randomUUID())!;
    session.category = $<HTMLSelectElement>('conversation-session-category')?.value
      || state.currentUser?.category || '';
  }

  const btn = orderButton();
  if (btn) btn.disabled = true;
  try {
    const order = await apiFetch<{ detail?: string }>('/api/deferred/orders', {
      method: 'POST',
      body: JSON.stringify({
        session_id: session.id,
        question,
        category: session.category || null,
      }),
    });
    input.value = '';
    session.draftInput = '';
    autoResizeTextarea();
    showToast(order.detail || 'Заказ принят.', { variant: 'info', duration: 12000 });
  } catch (err) {
    // 403 (нужна регистрация / не хватает средств) и 503 (услуга выключена)
    // приходят с человеческим detail — его и показываем.
    const code = err instanceof ApiError ? (err.body as { code?: string } | null)?.code : undefined;
    showToast(err instanceof ApiError ? err.message : 'Не удалось оформить заказ', { variant: 'error' });
    if (code === 'registration_required') showRegistrationPrompt();
  } finally {
    if (btn) btn.disabled = false;
  }
}
